/**
 * Select Component
 *
 * A comprehensive, accessible dropdown select component for the BiteWise application.
 * Supports search, validation states, and follows WCAG 2.1 AA guidelines.
 * Integrates with the existing design system and theme.
 *
 * @module components/ui/Select
 */

import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';

/**
 * Option interface for select items
 */
export interface SelectOption {
  /** Option value */
  value: string;
  /** Option label */
  label: string;
  /** Whether option is disabled */
  disabled?: boolean;
  /** Additional data for the option */
  data?: Record<string, unknown>;
}

/**
 * Select component props interface
 */
export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Select variant for different visual styles */
  variant?: 'default' | 'filled' | 'outlined';
  /** Select size */
  size?: 'sm' | 'md' | 'lg';
  /** Validation state */
  state?: 'default' | 'error' | 'warning' | 'success';
  /** Error message to display */
  error?: string;
  /** Warning message to display */
  warning?: string;
  /** Success message to display */
  success?: string;
  /** Helper text to display below select */
  helperText?: string;
  /** Label for the select */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Custom container class */
  containerClassName?: string;
  /** Custom label class */
  labelClassName?: string;
  /** Custom message class */
  messageClassName?: string;
  /** Options for the select */
  options?: SelectOption[];
  /** Placeholder text */
  placeholder?: string;
  /** Enable search functionality */
  searchable?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Custom render function for options */
  renderOption?: (option: SelectOption) => React.ReactNode;
  /** Callback when value changes */
  onValueChange?: (value: string) => void;
  /** Current selected value */
  value?: string;
  /** Default value */
  defaultValue?: string;
}

/**
 * Accessible Select component with search and validation states
 *
 * @example
 * ```tsx
 * <Select
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[
 *     { value: 'AU', label: 'Australia' },
 *     { value: 'US', label: 'United States' }
 *   ]}
 *   required
 *   searchable
 * />
 * ```
 */
export const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({
    className,
    containerClassName,
    labelClassName,
    messageClassName,
    variant = 'default',
    size = 'md',
    state = 'default',
    error,
    warning,
    success,
    helperText,
    label,
    required = false,
    leftIcon,
    loading = false,
    fullWidth = false,
    options = [],
    placeholder,
    searchable = false,
    searchPlaceholder = 'Search...',
    renderOption,
    onValueChange,
    value,
    defaultValue,
    id,
    'aria-describedby': ariaDescribedBy,
    ...props
  }, ref) => {
    const { isDark } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const selectRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);
    const hiddenSelectRef = useRef<HTMLSelectElement>(null);

    // Sync internal state with external value prop
    useEffect(() => {
      if (value !== undefined && value !== selectedValue) {
        setSelectedValue(value);
      }
    }, [value, selectedValue]);

    // Generate unique IDs for accessibility
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${selectId}-error`;
    const warningId = `${selectId}-warning`;
    const successId = `${selectId}-success`;
    const helperId = `${selectId}-helper`;
    const listboxId = `${selectId}-listbox`;

    // Determine which message to show (priority: error > warning > success > helper)
    const message = error || warning || success || helperText;
    const messageId = error ? errorId : warning ? warningId : success ? successId : helperId;

    // Build aria-describedby
    const describedBy = [
      ariaDescribedBy,
      message ? messageId : undefined
    ].filter(Boolean).join(' ') || undefined;

    // Filter options based on search term
    const filteredOptions = searchable && searchTerm
      ? options.filter(option =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.value.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;

    // Get selected option
    const selectedOption = options.find(option => option.value === selectedValue);

    // Handle value change
    const handleValueChange = (newValue: string) => {
      setSelectedValue(newValue);

      // Call both onChange and onValueChange for compatibility
      if (onValueChange) {
        onValueChange(newValue);
      }

      // Create synthetic event for onChange compatibility
      if (props.onChange) {
        const syntheticEvent = {
          target: { value: newValue },
          currentTarget: { value: newValue }
        } as React.ChangeEvent<HTMLSelectElement>;
        props.onChange(syntheticEvent);
      }

      setIsOpen(false);
      setSearchTerm('');
      setFocusedIndex(-1);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
          if (!isOpen) {
            setIsOpen(true);
            e.preventDefault();
          } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
            handleValueChange(filteredOptions[focusedIndex].value);
            e.preventDefault();
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchTerm('');
          setFocusedIndex(-1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setFocusedIndex(prev =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0
            );
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setFocusedIndex(prev =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1
            );
          }
          break;
        case 'Tab':
          setIsOpen(false);
          break;
      }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchTerm('');
          setFocusedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);

    // Scroll focused option into view
    useEffect(() => {
      if (focusedIndex >= 0 && optionsRef.current) {
        const focusedElement = optionsRef.current.children[focusedIndex] as HTMLElement;
        if (focusedElement) {
          focusedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    }, [focusedIndex]);

    // Base classes
    const baseClasses = 'relative w-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 cursor-pointer';

    // Variant classes
    const variantClasses = {
      default: cn(
        'border',
        isDark ? 'bg-secondary-800' : 'bg-white'
      ),
      filled: cn(
        'border-0',
        isDark ? 'bg-secondary-700' : 'bg-secondary-100'
      ),
      outlined: cn(
        'border-2 bg-transparent',
        isDark ? 'border-secondary-600' : 'border-secondary-300'
      )
    };

    // Size classes
    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5 min-h-[32px] rounded-md',
      md: 'text-base px-4 py-2 min-h-[40px] rounded-lg',
      lg: 'text-lg px-5 py-3 min-h-[48px] rounded-lg'
    };

    // State classes
    const stateClasses = {
      default: cn(
        'focus:ring-primary-500/50',
        isDark ? 'focus:border-primary-400' : 'focus:border-primary-500'
      ),
      error: cn(
        'focus:ring-red-500/50',
        isDark ? 'border-red-400 focus:border-red-400' : 'border-red-500 focus:border-red-500'
      ),
      warning: cn(
        'focus:ring-yellow-500/50',
        isDark ? 'border-yellow-400 focus:border-yellow-400' : 'border-yellow-500 focus:border-yellow-500'
      ),
      success: cn(
        'focus:ring-green-500/50',
        isDark ? 'border-green-400 focus:border-green-400' : 'border-green-500 focus:border-green-500'
      )
    };

    // Text color classes
    const textClasses = isDark ? 'text-secondary-100' : 'text-secondary-900';

    // Disabled classes
    const disabledClasses = cn(
      'disabled:opacity-50 disabled:cursor-not-allowed',
      isDark ? 'disabled:bg-secondary-800' : 'disabled:bg-secondary-50'
    );

    // Icon classes
    const iconClasses = cn(
      'flex-shrink-0',
      isDark ? 'text-secondary-400' : 'text-secondary-500'
    );

    // Loading spinner
    const LoadingSpinner = () => (
      <svg
        className="animate-spin h-4 w-4 text-secondary-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    // Chevron icon
    const ChevronIcon = () => (
      <svg
        className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180')}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );

    return (
      <div className={cn('space-y-1', fullWidth && 'w-full', containerClassName)}>
        {/* Hidden native select for form submission */}
        <select
          ref={hiddenSelectRef}
          id={selectId}
          value={selectedValue}
          onChange={() => {}} // Controlled by custom dropdown
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          {...props}
        >
          {placeholder && <option value="" className={isDark ? 'text-secondary-400' : 'text-secondary-200'}>{placeholder}</option>}
          {options.map(option => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'block text-sm font-medium',
              isDark ? 'text-secondary-300' : 'text-secondary-700',
              required && 'after:content-["*"] after:ml-0.5 after:text-red-500',
              labelClassName
            )}
          >
            {label}
          </label>
        )}

        {/* Custom Select */}
        <div ref={selectRef} className="relative">
          {/* Select Trigger */}
          <div
            ref={ref}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-labelledby={label ? `${selectId}-label` : undefined}
            aria-describedby={describedBy}
            aria-required={required}
            aria-invalid={state === 'error' ? 'true' : 'false'}
            tabIndex={0}
            className={cn(
              baseClasses,
              variantClasses[variant],
              sizeClasses[size],
              stateClasses[state],
              textClasses,
              disabledClasses,
              leftIcon && 'pl-10',
              'pr-10',
              className
            )}
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={handleKeyDown}
          >
            {/* Left Icon */}
            {leftIcon && (
              <div className={cn('absolute left-3 top-1/2 transform -translate-y-1/2', iconClasses)}>
                {leftIcon}
              </div>
            )}

            {/* Selected Value */}
            <span className={cn(
              'block truncate',
              leftIcon && 'ml-7',
              (!selectedOption || selectedOption.value === '') && (isDark ? 'text-secondary-400' : 'text-secondary-200')
            )}>
              {selectedOption ? selectedOption.label : placeholder || 'Select an option'}
            </span>

            {/* Right Icon */}
            <div className={cn('absolute right-3 top-1/2 transform -translate-y-1/2', iconClasses)}>
              {loading ? <LoadingSpinner /> : <ChevronIcon />}
            </div>
          </div>

          {/* Dropdown */}
          {isOpen && (
            <div className={cn(
              "absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-hidden",
              isDark ? "bg-secondary-800 border-secondary-600" : "bg-white border-secondary-300"
            )}>
              {/* Search Input */}
              {searchable && (
                <div className={cn(
                  "p-2 border-b",
                  isDark ? "border-secondary-700" : "border-secondary-200"
                )}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={cn(
                      "w-full px-3 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary-500",
                      isDark
                        ? "border-secondary-600 bg-secondary-700 text-secondary-100 placeholder:text-secondary-400"
                        : "border-secondary-300 bg-white text-secondary-900 placeholder:text-secondary-200"
                    )}
                  />
                </div>
              )}

              {/* Options */}
              <div
                ref={optionsRef}
                role="listbox"
                id={listboxId}
                className="max-h-48 overflow-y-auto"
              >
                {filteredOptions.length === 0 ? (
                  <div className={cn(
                    "px-4 py-2 text-sm",
                    isDark ? "text-secondary-400" : "text-secondary-500"
                  )}>
                    No options found
                  </div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={option.value === selectedValue}
                      className={cn(
                        'px-4 py-2 cursor-pointer transition-colors duration-150',
                        isDark ? 'hover:bg-secondary-700' : 'hover:bg-secondary-100',
                        option.value === selectedValue && (isDark ? 'bg-primary-900 text-primary-100' : 'bg-primary-100 text-primary-900'),
                        index === focusedIndex && (isDark ? 'bg-secondary-700' : 'bg-secondary-100'),
                        option.disabled && 'opacity-50 cursor-not-allowed'
                      )}
                      onClick={() => !option.disabled && handleValueChange(option.value)}
                    >
                      {renderOption ? renderOption(option) : option.label}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Message */}
        {message && (
          <p
            id={messageId}
            className={cn(
              'text-sm',
              error && (isDark ? 'text-red-400' : 'text-red-600'),
              warning && (isDark ? 'text-yellow-400' : 'text-yellow-600'),
              success && (isDark ? 'text-green-400' : 'text-green-600'),
              !error && !warning && !success && (isDark ? 'text-secondary-400' : 'text-secondary-600'),
              messageClassName
            )}
            role={error ? 'alert' : undefined}
            aria-live={error ? 'polite' : undefined}
          >
            {message}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;